<script lang="ts" setup>
import { onMounted, ref } from 'vue'

import BusyIndicator from '@/components/common/BusyIndicator.vue'
import CardContainer from '@/components/common/CardContainer.vue'
import NotificationBanner from '@/components/common/NotificationBanner.vue'
import PrintButton from '@/components/common/PrintButton.vue'
import LeagueRundownTable from '@/components/widgets/public/LeagueRundownTable.vue'
import LeagueStandingsTable from '@/components/widgets/public/LeagueStandingsTable.vue'
import { useLeaguesStore } from '@/store/public/leagues'
import { getErrorMessage } from '@/utils/error-handling'
import { formatDate } from '@/utils/format'

const leagues = useLeaguesStore()

const errorMessage = ref<string | null>(null)
const isLoading = ref<boolean>(false)

onMounted(async () => {
  errorMessage.value = null
  isLoading.value = true

  try {
    await leagues.fetchLeagueStandings()
  } catch (e) {
    const message = getErrorMessage(e as Error)
    console.error(`Failed fetching league standings: ${message}`)
    errorMessage.value = message
  }

  isLoading.value = false
})
</script>

<template>
  <h1 v-if="isLoading" class="busy-indicator-container">
    <BusyIndicator />
  </h1>

  <NotificationBanner
    v-else-if="errorMessage"
    :message="errorMessage"
    is-danger
  />

  <NotificationBanner
    v-else-if="(leagues.selectedLeague?.standings?.sections ?? []).length === 0"
    :message="$t('leagues.errors.no-league-results')"
    is-warning
  />

  <template v-else>
    <h4>
      {{ $t('leagues.labels.last-updated') }}:
      <span class="last-updated">
        {{ formatDate(new Date(leagues.selectedLeague?.standings?.timestamp)) }}
      </span>
    </h4>

    <div class="table-controls">
      <PrintButton />
    </div>

    <CardContainer
      v-for="(section, i) in leagues.selectedLeague?.standings?.sections"
      :key="`section-${i}`"
      :title="$t('leagues.labels.section', { section: section.number })"
    >
      <NotificationBanner
        v-if="(section.rows ?? []).length === 0"
        :message="$t('leagues.errors.no-section-results')"
        is-warning
      />

      <LeagueStandingsTable v-else :rows="section.rows" />

      <template v-if="(section?.rundown ?? []).length > 0">
        <h4>{{ $t('leagues.labels.rundown') }}</h4>
        <LeagueRundownTable :rows="section.rundown" />
      </template>
    </CardContainer>
  </template>
</template>

<style lang="scss" scoped>
.busy-indicator-container {
  text-align: center;
}

.last-updated {
  font-weight: normal;
}
</style>
